import { useEffect, useMemo, useState } from 'react';
import { useFormState } from 'react-final-form';

import { type FWaypointMessageFragment, usePreviewWaypointTemplateMutation } from '@dashboard/generated/wp/platform';
import parseJsonObject from '@dashboard/utilities/parseJsonObject';
import useThrottledValue from '@wp-react/hooks/useThrottledValue';

import { useCurrentPreviewVariables } from '../hooks';

type TResult = {
  loading: boolean;
  error?: string;
  data?: FWaypointMessageFragment;
};

export default function useMessagePreview() {
  const [error, setError] = useState<string | undefined>(undefined);
  const [previewTemplate, { loading, data }] = usePreviewWaypointTemplateMutation({});

  const { values } = useFormState();
  const { input } = useCurrentPreviewVariables();
  const value = useThrottledValue(input.value, 500);
  const configuration = useThrottledValue(values.configuration, 500);

  let dataFragment: FWaypointMessageFragment | undefined = undefined;
  dataFragment = useMemo(() => {
    if (data) {
      return data.previewWaypointTemplate;
    }
    return dataFragment;
  }, [dataFragment, data]);

  useEffect(() => {
    let variables: Record<string, unknown>;
    try {
      variables = parseJsonObject(value);
    } catch (e) {
      setError('There is an error in your test data. Correct your JSON variables to see the preview.');
      return;
    }
    const callb = async () => {
      try {
        await previewTemplate({ variables: { variables, configuration } });
        setError(undefined);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Unknown error';
        setError('1' + message);
      }
    };
    callb();
  }, [value, configuration, previewTemplate, setError]);

  return useMemo<TResult>(() => ({ loading, error, data: dataFragment }), [loading, error, dataFragment]);
}
